import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const IssueCard = ({ issue, index }) => {
  const bullets = [
    ...issue.bullets
  ];
  
  return (
    <Card className="overflow-hidden shadow-lg mb-12 bg-white">
      <div className="grid lg:grid-cols-2 gap-0">
        <div className="w-full h-full min-h-[350px] lg:min-h-[450px] overflow-hidden bg-white">
          <img
            src={issue.image}
            alt={issue.title}
            className="w-full h-full object-cover"
          />
        </div>
        <CardContent className="p-8 lg:p-12 bg-white">
          <h2 className="text-3xl font-bold text-navy-900 mb-6">{issue.title}</h2>
          <div className="space-y-3 mb-6">
            {bullets.map((bullet, idx) => (
              <div key={`bullet-${idx}`} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                <p className="text-gray-700">{bullet}</p>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 border-l-4 border-red-600 p-6 rounded-r-lg">
            <h3 className="font-bold text-navy-900 mb-2">Why This Matters</h3>
            <p className="text-gray-700">{issue.whyItMatters}</p>
          </div>
        </CardContent>
      </div>
      <div className="flex flex-wrap justify-center gap-4 p-8 border-t">
        <Link to="/donate">
          <Button className="bg-navy-900 hover:bg-navy-800 text-white font-semibold">Volunteer</Button>
        </Link>
        <a href="https://buy.stripe.com/4gMeVec2v4oP6XP3sQ6g803" target="_blank" rel="noopener noreferrer">
          <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold">Donate</Button>
        </a>
      </div>
    </Card>
  );
};

const Issues = () => {
  const issuesData = [
    {
      id: "education",
      title: "Supporting School Choice",
      image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=85",
      bullets: [
        "Empower parents to choose the best schools",
        "Expand high-quality charter schools",
        "Improve public schools & raise standards",
        "Focus on literacy & career readiness"
      ],
      whyItMatters: "Parents know what's best for their children. By expanding educational options and raising standards across all schools, we give every family access to quality education."
    },
    {
      id: "safety",
      title: "Public Safety",
      image: "https://images.unsplash.com/photo-1601848095050-b1f765e83937?w=1200&q=85",
      bullets: [
        "Support law enforcement and community policing",
        "Repeal dangerous bail reforms",
        "Increase penalties for violent crime",
        "Prioritize victims' rights and accountability",
        "Ensure prosecutors enforce the law"
      ],
      whyItMatters: "Safe communities are the foundation of thriving neighborhoods. We must support those who protect us and hold criminals accountable."
    },
    {
      id: "affordability",
      title: "Fight for Affordability",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=85",
      bullets: [
        "Cut middle-class taxes and stop runaway spending",
        "Eliminate waste and bureaucratic bloat",
        "Lower energy costs by opposing utility mandates",
        "Support small businesses with tax relief",
        "Reduce housing costs through responsible development"
      ],
      whyItMatters: "New York families are struggling with rising costs. We need to cut wasteful spending, lower taxes, and make our state affordable again."
    },
    {
      id: "mental-health",
      title: "Mental Health",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=85",
      bullets: [
        "Expand access for kids, veterans, and seniors",
        "Increase crisis-treatment capacity",
        "Improve coordination between hospitals and courts",
        "Support mental health for first responders",
        "Focus on treatment and recovery"
      ],
      whyItMatters: "Mental health challenges affect families across our community. By expanding treatment options, we can help those in crisis get support."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="py-16 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-white">Where I Stand</h1>
          <p className="text-xl text-white font-semibold">Common-sense solutions for New York families</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <IssueCard issue={issuesData[0]} index={0} />
          <IssueCard issue={issuesData[1]} index={1} />
          <IssueCard issue={issuesData[2]} index={2} />
          <IssueCard issue={issuesData[3]} index={3} />
        </div>
      </section>

      <section className="py-16 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Make a Difference?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our campaign and help bring common-sense solutions to Albany.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/volunteer">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8">
                Join Our Team <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/donate">
              <Button size="lg" className="bg-navy-900 hover:bg-navy-800 text-white px-8">
                Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Issues;
